<template>
  <v-container fluid class="pa-3 pa-sm-4 schedule-page">
    <!-- Header -->
    <div class="d-flex align-center mb-3 flex-wrap ga-2">
      <h1 class="text-h5 text-sm-h4 mb-0 d-flex align-center">
        <v-icon icon="mdi-calendar-account" class="me-2" color="primary" />
        {{ readOnly ? $t('adminUsers.viewAllSchedules') : $t('adminSchedule.title') }}
      </h1>
      <v-spacer />
      <v-text-field
        v-model="search"
        :placeholder="$t('common.search')"
        prepend-inner-icon="mdi-magnify"
        density="compact"
        hide-details
        clearable
        variant="solo-filled"
        flat
        style="max-width: 240px;"
      />
    </div>

    <!-- Controls: instructor filter + date nav -->
    <v-card variant="tonal" class="mb-3">
      <v-card-text class="d-flex align-center ga-2 flex-wrap py-2">
        <v-select
          v-model="selectedInstructorId"
          :items="instructorFilterItems"
          item-title="title"
          item-value="value"
          density="compact"
          variant="outlined"
          hide-details
          prepend-inner-icon="mdi-account-tie"
          style="max-width: 240px; min-width: 180px;"
        />
        <v-spacer />
        <v-btn icon="mdi-chevron-left" size="small" variant="text" @click="shiftDays(-7)" />
        <div class="font-weight-bold text-body-2 text-no-wrap">{{ rangeLabel }}</div>
        <v-btn icon="mdi-chevron-right" size="small" variant="text" @click="shiftDays(7)" />
        <v-btn size="small" variant="text" @click="goToToday">{{ $t('adminSchedule.today') }}</v-btn>
        <v-btn icon="mdi-refresh" size="small" variant="text" :loading="loading" @click="loadAll" />
      </v-card-text>
    </v-card>

    <!-- Loading -->
    <div v-if="loading && !instructors.length" class="text-center pa-12">
      <v-progress-circular indeterminate color="primary" size="48" />
    </div>

    <!-- Board -->
    <LessonBoard
      v-else
      ref="boardRef"
      :lessons="boardLessons"
      :days-off="boardDaysOff"
      :show-instructor="selectedInstructorId === 'all'"
      :empty-text="$t('adminSchedule.noLessons')"
      @toggle="readOnly ? undefined : toggleComplete"
      @open="openDetail"
      @remove-dayoff="readOnly ? undefined : removeDayOff"
      @download-waybill="downloadWaybill"
    />

    <!-- FAB with speed-dial menu (Lesson / Day off) -->
    <v-menu v-if="!readOnly" location="top end" :offset="12">
      <template #activator="{ props: menuProps }">
        <v-btn
          icon="mdi-plus"
          color="#b08d57"
          size="large"
          class="fab"
          :disabled="!instructors.length"
          v-bind="menuProps"
        />
      </template>
      <v-list density="comfortable" class="fab-menu" rounded="lg">
        <v-list-item prepend-icon="mdi-account-plus" :title="$t('schedule.wizard.addLesson')" @click="openWizard" />
        <v-list-item prepend-icon="mdi-beach" :title="$t('dayOff.add')" @click="openDayOff" />
      </v-list>
    </v-menu>

    <!-- Wizard -->
    <LessonWizard
      v-model="wizardOpen"
      :students="students"
      :time-slots="wizardTimeSlots"
      :addresses="wizardAddresses"
      :booked-lessons="allLessons"
      :instructors="instructors.map(i => ({ id: i.id, fio: i.fio }))"
      :default-instructor-id="wizardInstructorId"
      :default-date="todayIso"
      :busy="saving"
      @submit="onWizardSubmit"
    />

    <!-- Day off dialog -->
    <DayOffDialog
      v-model="dayOffOpen"
      :instructors="instructors.map(i => ({ id: i.id, fio: i.fio }))"
      :default-instructor-id="wizardInstructorId"
      :default-date="todayIso"
      :busy="savingDayOff"
      @submit="onDayOffSubmit"
    />

    <!-- Day-off conflicts dialog -->
    <v-dialog v-model="conflictDialog.visible" max-width="460" :persistent="conflictDialog.busy">
      <v-card>
        <v-card-title class="d-flex align-center ga-2 text-wrap">
          <v-icon color="warning">mdi-alert-circle-outline</v-icon>
          <span class="text-body-1 font-weight-bold">{{ $t('dayOff.conflictTitle') }}</span>
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-3">{{ $t('dayOff.conflictHint', { n: conflictDialog.conflicts.length }) }}</p>
          <v-list density="compact" class="conflict-list" rounded="lg">
            <v-list-item
              v-for="c in conflictDialog.conflicts"
              :key="c.id"
              :title="`${c.dateIso.slice(8,10)}.${c.dateIso.slice(5,7)} · ${c.timeSlot}`"
              :subtitle="`${c.studentName}${c.phone ? ' · ' + c.phone : ''}`"
              prepend-icon="mdi-calendar-clock"
            />
          </v-list>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn variant="text" :disabled="conflictDialog.busy" @click="keepConflicts">{{ $t('dayOff.keepLessons') }}</v-btn>
          <v-btn color="error" variant="flat" :loading="conflictDialog.busy" @click="cancelConflicts">
            {{ $t('dayOff.cancelLessons') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit dialog -->
    <LessonEditDialog
      v-if="!readOnly"
      v-model="detail.visible"
      :lesson="detail.lesson"
      :addresses="detailAddresses"
      :busy="detail.busy"
      @save="saveLessonEdit"
      @toggle-complete="toggleComplete(detail.lesson)"
      @cancel-lesson="cancelLesson"
      @add-address="onAddAddress"
      @update-address="onUpdateAddress"
    />
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import api from '@/utils/api'
import { useSnackbarStore } from '@/stores/snackbar'
import { useSocketStore } from '@/stores/socket'
import { useAuthStore } from '@/stores/auth'
import LessonBoard from '@/components/schedule/LessonBoard.vue'
import LessonWizard from '@/components/schedule/LessonWizard.vue'
import LessonEditDialog from '@/components/schedule/LessonEditDialog.vue'
import DayOffDialog from '@/components/schedule/DayOffDialog.vue'
import { downloadWaybillPdf } from '@/utils/waybillPdf'

const { t, locale } = useI18n()
const snackbar = useSnackbarStore()
const socketStore = useSocketStore()
const authStore = useAuthStore()
const route = useRoute()

const VISIBLE_DAYS = 14
const DEFAULT_TIME_SLOTS = [
  '08:00-09:00', '09:00-10:00', '10:00-11:00', '11:00-12:00',
  '12:00-13:00', '13:00-14:00', '14:00-15:00', '15:00-16:00',
  '16:00-17:00', '17:00-18:00', '18:00-19:00', '19:00-20:00'
]
const LOCALE_MAP = { kz: 'kk-KZ', kk: 'kk-KZ', ru: 'ru-RU', en: 'en-US' }

const loading = ref(false)
const saving = ref(false)
const search = ref('')
const instructors = ref([])
const bookingsByInstructor = ref({})
const students = ref([])
const dateOffset = ref(0)
const selectedInstructorId = ref('all')
const wizardOpen = ref(false)
const dayOffOpen = ref(false)
const savingDayOff = ref(false)
const daysOffByInstructor = ref({})
const conflictDialog = ref({ visible: false, instructorId: null, conflicts: [], busy: false })
const detail = ref({ visible: false, lesson: null, busy: false })
const boardRef = ref(null)
const isPrivilegedInstructor = computed(() => (
  authStore.user?.role === 'INSTRUCTOR' && authStore.user?.canViewAllSchedules === true
))
const canManageSchedules = computed(() => authStore.user?.role === 'ADMIN' || isPrivilegedInstructor.value)
const readOnly = computed(() => route.meta?.readOnly === true || !canManageSchedules.value)
const instructorSchedulePath = (instructorId, suffix) => (
  readOnly.value
    ? `/instructor/all-schedules/${instructorId}/${suffix}`
    : `/admin/instructors/${instructorId}/${suffix}`
)

// ── Helpers ──
const toLocalIso = (d) => {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}
const todayIso = computed(() => toLocalIso(new Date()))
const cleanPhone = (p) => String(p || '').replace(/[^\d+]/g, '')
const intlLocale = computed(() => LOCALE_MAP[locale.value] || 'kk-KZ')

const formatFullDate = (iso) => {
  if (!iso) return ''
  return new Date(iso + 'T00:00:00').toLocaleDateString(intlLocale.value, { year: 'numeric', month: 'long', day: 'numeric' })
}

const rangeLabel = computed(() => {
  const start = new Date(); start.setDate(start.getDate() + dateOffset.value)
  const end = new Date(); end.setDate(end.getDate() + dateOffset.value + VISIBLE_DAYS - 1)
  const fmt = (d) => d.toLocaleDateString(intlLocale.value, { day: 'numeric', month: 'short' })
  return `${fmt(start)} — ${fmt(end)}`
})

const statusColor = (s) => ({ SCHEDULED: 'primary', COMPLETED: 'success', CANCELLED: 'error', RESCHEDULED: 'warning' }[s] || 'grey')
const statusLabel = (s) => t(`schedule.status.${s}`)

const parseFreeTime = (raw) => {
  if (!raw) return []
  let arr = raw
  if (typeof raw === 'string') { try { arr = JSON.parse(raw) } catch { return [] } }
  if (Array.isArray(arr)) return [...new Set(arr.filter(Boolean))]
  if (arr && typeof arr === 'object') return [...new Set(Object.values(arr).flat().filter(Boolean))]
  return []
}
const sortSlots = (slots) => [...slots].sort((a, b) => {
  const ma = String(a).match(/^(\d{1,2}):(\d{2})/); const mb = String(b).match(/^(\d{1,2}):(\d{2})/)
  return (ma ? +ma[1] * 60 + +ma[2] : 0) - (mb ? +mb[1] * 60 + +mb[2] : 0)
})

const instructorAddresses = (ins) => {
  if (!ins) return []
  const list = ins.addresses?.length ? ins.addresses : (ins.address ? [ins.address] : [])
  return list.map(a => ({ id: a.id, address: a.address || a.name || '', name: a.name || '' }))
}

// Global address map: id -> text
const addressMap = computed(() => {
  const map = new Map()
  for (const ins of instructors.value) {
    for (const a of instructorAddresses(ins)) {
      map.set(a.id, a.address || a.name || '')
    }
  }
  return map
})

// ── Normalized lessons ──
const allLessons = computed(() => {
  const out = []
  for (const ins of instructors.value) {
    const list = bookingsByInstructor.value[ins.id] || []
    for (const b of list) {
      out.push({
        id: b.id,
        instructorId: ins.id,
        instructorName: ins.fio,
        carModel: ins.carModel || '',
        dateIso: toLocalIso(new Date(b.date)),
        timeSlot: b.timeSlot,
        status: b.status,
        studentName: b.student?.fio || '',
        phone: b.student?.phone || '',
        addressText: b.addressId ? (addressMap.value.get(b.addressId) || '') : '',
        note: b.notes || '',
        raw: b
      })
    }
  }
  return out
})

const filteredInstructors = computed(() => {
  const term = (search.value || '').toLowerCase().trim()
  if (!term) return instructors.value
  return instructors.value.filter(i => (i.fio || '').toLowerCase().includes(term))
})

const boardLessons = computed(() => {
  const term = (search.value || '').toLowerCase().trim()
  return allLessons.value.filter(l => {
    if (l.status === 'CANCELLED') return false
    if (selectedInstructorId.value !== 'all' && l.instructorId !== selectedInstructorId.value) return false
    if (term) {
      const hay = `${l.studentName} ${l.phone} ${l.instructorName}`.toLowerCase()
      if (!hay.includes(term) && !hay.replace(/\D/g, '').includes(term.replace(/\D/g, ''))) return false
    }
    return true
  })
})

const downloadWaybill = async (lesson) => {
  try {
    await downloadWaybillPdf(lesson)
  } catch {
    snackbar.error('Жол парағын жасау мүмкін болмады / Не удалось сформировать путевой лист')
  }
}

const instructorFilterItems = computed(() => [
  { title: t('adminSchedule.allInstructors'), value: 'all' },
  ...filteredInstructors.value.map(i => ({ title: i.fio, value: i.id }))
])

// ── Days off, expanded into per-day markers for the board ──
const instructorNameById = computed(() => {
  const m = new Map()
  for (const ins of instructors.value) m.set(ins.id, ins.fio)
  return m
})

const boardDaysOff = computed(() => {
  const out = []
  for (const [insId, list] of Object.entries(daysOffByInstructor.value)) {
    const instructorId = Number(insId)
    if (selectedInstructorId.value !== 'all' && instructorId !== selectedInstructorId.value) continue
    for (const d of (list || [])) {
      const start = new Date(d.startDate); start.setHours(0, 0, 0, 0)
      const end = new Date(d.endDate); end.setHours(0, 0, 0, 0)
      const cursor = new Date(start)
      let guard = 0
      while (cursor.getTime() <= end.getTime() && guard < 370) {
        out.push({
          id: d.id,
          instructorId,
          instructorName: instructorNameById.value.get(instructorId) || '',
          dateIso: toLocalIso(cursor),
          startTime: d.startTime || '',
          endTime: d.endTime || '',
          reason: d.reason || ''
        })
        cursor.setDate(cursor.getDate() + 1)
        guard += 1
      }
    }
  }
  return out
})

// ── Wizard inputs ──
const wizardInstructorId = computed(() => selectedInstructorId.value === 'all' ? (instructors.value[0]?.id ?? null) : selectedInstructorId.value)
const currentWizardInstructor = computed(() => instructors.value.find(i => i.id === wizardInstructorId.value) || null)
const wizardTimeSlots = computed(() => {
  const ins = currentWizardInstructor.value
  const slots = ins ? sortSlots(parseFreeTime(ins.freeTime)) : []
  return slots.length ? slots : DEFAULT_TIME_SLOTS
})
const wizardAddresses = computed(() => instructorAddresses(currentWizardInstructor.value))

const openWizard = () => { wizardOpen.value = true }
const openDayOff = () => { dayOffOpen.value = true }

// ── Day off submit / delete ──
const onDayOffSubmit = async (payload) => {
  savingDayOff.value = true
  try {
    const res = await api.post(`/admin/instructors/${payload.instructorId}/days-off`, {
      startDate: payload.startDate,
      endDate: payload.endDate,
      startTime: payload.startTime || '',
      endTime: payload.endTime || '',
      reason: payload.reason || ''
    })
    snackbar.success(t('dayOff.saved'))
    dayOffOpen.value = false
    await loadAllBookings()
    // If there are existing lessons on the day off — ask what to do
    const conflicts = res.data?.conflicts || []
    if (conflicts.length) {
      conflictDialog.value = { visible: true, instructorId: payload.instructorId, conflicts, busy: false }
    }
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    savingDayOff.value = false
  }
}

// Cancel all conflicting lessons (and notify students)
const cancelConflicts = async () => {
  const { instructorId, conflicts } = conflictDialog.value
  conflictDialog.value.busy = true
  try {
    for (const c of conflicts) {
      await api.put(`/admin/instructors/${instructorId}/schedule/${c.id}`, { status: 'CANCELLED', notify: true })
    }
    snackbar.success(t('dayOff.conflictsCancelled'))
    conflictDialog.value.visible = false
    await loadAllBookings()
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    conflictDialog.value.busy = false
  }
}
const keepConflicts = () => { conflictDialog.value.visible = false }

const removeDayOff = async (item) => {
  try {
    await api.delete(`/admin/instructors/${item.instructorId}/days-off/${item.id}`)
    snackbar.success(t('dayOff.removed'))
    await loadAllBookings()
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  }
}

// ── Navigation ──
const scrollToToday = () => nextTick(() => boardRef.value?.scrollToToday())
const shiftDays = (delta) => { dateOffset.value += delta; loadAllBookings() }
const goToToday = async () => { dateOffset.value = 0; await loadAllBookings(); scrollToToday() }

// ── API ──
const loadInstructors = async () => {
  loading.value = true
  try {
    // ALL instructor-role users with guaranteed profiles (self-heals broken instructorId links)
    const res = await api.get(readOnly.value ? '/instructor/all-schedules/instructors' : '/admin/schedule-instructors')
    instructors.value = res.data?.instructors || []
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    loading.value = false
  }
}

const loadStudents = async () => {
  if (readOnly.value) return
  try {
    if (isPrivilegedInstructor.value) {
      const res = await api.get('/instructor/all-schedules/students')
      students.value = (res.data?.students || []).map(u => ({ id: u.id, fio: u.fio, phone: u.phone }))
      return
    }

    const [s1, s2] = await Promise.all([
      api.get('/admin/users', { params: { role: 'STUDENT', limit: 1000 } }),
      api.get('/admin/users', { params: { role: 'LIMITED_STUDENT', limit: 1000 } })
    ])
    const all = [...(s1.data?.users || []), ...(s2.data?.users || [])]
    students.value = all.map(u => ({ id: u.id, fio: u.fio, phone: u.phone }))
  } catch { /* ignore */ }
}

const loadAllBookings = async () => {
  const first = new Date(); first.setDate(first.getDate() + dateOffset.value - 1)
  const last = new Date(); last.setDate(last.getDate() + dateOffset.value + VISIBLE_DAYS + 1)
  const start = toLocalIso(first); const end = toLocalIso(last)
  const tasks = instructors.value.map(ins =>
    api.get(instructorSchedulePath(ins.id, 'schedule'), { params: { startDate: start, endDate: end } })
      .then(r => ({ id: ins.id, schedules: r.data?.schedules || [] }))
      .catch(() => ({ id: ins.id, schedules: [] }))
  )
  const results = await Promise.all(tasks)
  const map = {}
  results.forEach(r => { map[r.id] = r.schedules })
  bookingsByInstructor.value = map

  // Days off for the same window (overlap)
  const offTasks = instructors.value.map(ins =>
    api.get(instructorSchedulePath(ins.id, 'days-off'), { params: { startDate: start, endDate: end } })
      .then(r => ({ id: ins.id, daysOff: r.data?.daysOff || [] }))
      .catch(() => ({ id: ins.id, daysOff: [] }))
  )
  const offResults = await Promise.all(offTasks)
  const offMap = {}
  offResults.forEach(r => { offMap[r.id] = r.daysOff })
  daysOffByInstructor.value = offMap
}

const loadAll = async () => {
  await loadInstructors()
  await Promise.all([loadAllBookings(), loadStudents()])
}

// ── Wizard submit: create client (if new) + create lesson ──
const onWizardSubmit = async (payload) => {
  saving.value = true
  try {
    let studentId = payload.studentId
    if (!studentId) {
      // Create a new student linked to the instructor
      const res = await api.post('/admin/users', {
        fio: payload.fio,
        phone: payload.phone,
        role: 'LIMITED_STUDENT',
        instructorId: payload.instructorId
      })
      studentId = res.data?.user?.id
      if (!studentId) throw new Error('student create failed')
      snackbar.success(t('schedule.wizard.clientCreated'))
    }
    await api.post(`/admin/instructors/${payload.instructorId}/schedule`, {
      studentId,
      date: payload.date,
      timeSlots: [payload.timeSlot],
      addressId: payload.addressId || undefined,
      notes: payload.note || '',
      notify: true
    })
    snackbar.success(t('schedule.wizard.lessonSaved'))
    wizardOpen.value = false
    await Promise.all([loadAllBookings(), loadStudents()])
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    saving.value = false
  }
}

// ── Toggle completed (checkbox) ──
const toggleComplete = async (lesson) => {
  const next = lesson.status === 'COMPLETED' ? 'SCHEDULED' : 'COMPLETED'
  if (detail.value.visible) detail.value.busy = true
  try {
    await api.put(`/admin/instructors/${lesson.instructorId}/schedule/${lesson.id}`, { status: next, notify: false })
    // update locally
    const list = bookingsByInstructor.value[lesson.instructorId] || []
    const b = list.find(x => x.id === lesson.id)
    if (b) b.status = next
    if (detail.value.lesson?.id === lesson.id) detail.value.lesson.status = next
    snackbar.success(next === 'COMPLETED' ? t('adminSchedule.markedDone') : t('adminSchedule.markedScheduled'))
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    detail.value.busy = false
  }
}

const cancelLesson = async () => {
  const lesson = detail.value.lesson
  if (!lesson) return
  detail.value.busy = true
  try {
    await api.put(`/admin/instructors/${lesson.instructorId}/schedule/${lesson.id}`, { status: 'CANCELLED', notify: true })
    snackbar.success(t('adminSchedule.bookingCancelled'))
    detail.value.visible = false
    await loadAllBookings()
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    detail.value.busy = false
  }
}

const openDetail = (lesson) => {
  if (readOnly.value) return
  detail.value = { visible: true, lesson: { ...lesson }, busy: false }
}

// Addresses of the lesson's instructor (for the edit dialog)
const detailAddresses = computed(() => {
  const ins = instructors.value.find(i => i.id === detail.value.lesson?.instructorId)
  return instructorAddresses(ins)
})

// Add a new address for the lesson's instructor (create global + assign)
const onAddAddress = async (payload, cb) => {
  const insId = detail.value.lesson?.instructorId
  const ins = instructors.value.find(i => i.id === insId)
  if (!ins) { cb && cb(null); return }
  try {
    const name = (payload.name && payload.name.trim()) || payload.address
    const res = await api.post('/admin/addresses', { name, address: payload.address })
    const created = res.data
    const currentIds = instructorAddresses(ins).map(a => a.id)
    await api.put(`/admin/instructors/${insId}/addresses`, { addressIds: [...new Set([...currentIds, created.id])] })
    await loadInstructors()
    snackbar.success(t('instructor.schedule.addressAdded'))
    cb && cb(created)
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
    cb && cb(null)
  }
}
const onUpdateAddress = async (payload, cb) => {
  try {
    await api.put(`/admin/addresses/${payload.id}`, { address: payload.address })
    await loadInstructors()
    snackbar.success(t('instructor.schedule.addressUpdated'))
    cb && cb(true)
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
    cb && cb(false)
  }
}

const saveLessonEdit = async (changes) => {
  const lesson = detail.value.lesson
  if (!lesson) return
  detail.value.busy = true
  try {
    await api.put(`/admin/instructors/${lesson.instructorId}/schedule/${lesson.id}`, {
      date: changes.date,
      timeSlot: changes.timeSlot,
      addressId: changes.addressId ?? null,
      notes: changes.note,
      notify: false
    })
    snackbar.success(t('schedule.wizard.lessonSaved'))
    detail.value.visible = false
    await loadAllBookings()
  } catch (e) {
    snackbar.error(e.response?.data?.error || t('common.error'))
  } finally {
    detail.value.busy = false
  }
}

// ── Real-time sync ──
let reloadTimer = null
const handleScheduleChanged = () => {
  clearTimeout(reloadTimer)
  reloadTimer = setTimeout(() => { loadAllBookings() }, 300)
}
const registerSync = () => {
  socketStore.offScheduleChanged(handleScheduleChanged)
  socketStore.onScheduleChanged(handleScheduleChanged)
}
watch(() => socketStore.connected, (c) => { if (c) registerSync() })

onMounted(async () => {
  await loadAll()
  scrollToToday()
  registerSync()
})
onUnmounted(() => {
  socketStore.offScheduleChanged(handleScheduleChanged)
  clearTimeout(reloadTimer)
})
</script>

<style scoped>
.schedule-page { padding-bottom: 90px; }
.fab-menu { min-width: 200px; }
.fab {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1999;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.45);
}
/* Above mobile bottom nav on small screens */
@media (max-width: 959px) {
  .fab {
    bottom: 88px;
    right: 20px;
  }
}
@media (max-width: 480px) {
  .fab {
    bottom: 96px;
    right: 16px;
  }
}
</style>
